import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

/**
 * Two-pane layout used by sign-in and sign-up. Form on the left, brand panel
 * on the right with the mascot floating + the open-book-with-tree motif.
 *
 * Single column on mobile (form first, brand below).
 */
export function AuthLayout({ children, eyebrow }: { children: ReactNode; eyebrow?: string }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Form pane */}
      <div className="relative flex flex-col px-6 py-10 sm:px-10">
        <header className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/brand/eduborg-mascot.png"
              alt=""
              aria-hidden
              width={32}
              height={32}
              className="animate-mascot-idle"
            />
            <span className="font-display text-xl font-bold tracking-tight">
              <span className="text-primary">Edu</span>
              <span className="text-accent">Borg</span>
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            ← Back home
          </Link>
        </header>

        <motion.main
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="flex flex-1 flex-col justify-center"
        >
          <div className="mx-auto w-full max-w-sm space-y-6">
            {eyebrow && (
              <span className="inline-block rounded-full border border-border bg-card/80 px-3 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {eyebrow}
              </span>
            )}
            {children}
          </div>
        </motion.main>
      </div>

      {/* Brand pane */}
      <aside className="relative hidden overflow-hidden bg-gradient-to-br from-primary via-primary to-[hsl(220,80%,18%)] text-primary-foreground lg:block">
        <span
          aria-hidden
          className="absolute -left-32 -top-32 h-72 w-72 rounded-full bg-accent/30 blur-3xl"
        />
        <span
          aria-hidden
          className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-gold/20 blur-3xl"
        />
        <div className="relative flex h-full flex-col items-center justify-center px-12 text-center">
          <motion.img
            src="/brand/hero-tree.png"
            alt=""
            aria-hidden
            className="w-72 max-w-full"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <h2 className="mt-6 max-w-md font-display text-3xl font-bold leading-tight">
            Your classroom has never been this intelligent.
          </h2>
          <p className="mt-3 max-w-sm text-primary-foreground/80">
            EduBorg watches, suggests, drafts, and celebrates &mdash; in the corner of every page.
          </p>
        </div>
      </aside>
    </div>
  );
}
